import {
  Button,
  ButtonTheme,
  CheckboxField,
  IStyleConfig,
  mergeStyles,
  OutsideClick,
  Popup,
  PopupArrow,
} from '@cian/components';

import * as React from 'react';

import { IJsonQuery } from '../../../json_query';
import { ECoworkingOfferType } from '../../../repositories/common/json_query';
import { dealTypeFromJsonQuery, offerTypeFromJsonQuery } from '../../../utils/category';

import { CheckboxLabel } from './internal/components/CheckboxLabel';

const style = require('./index.css');

export interface ICoworkingOfferTypeProps {
  jsonQuery: IJsonQuery;
  buttonStyle?: IStyleConfig;
  onOfferTypeSelected(value: ECoworkingOfferType): void;
  isCoworkingWorkplaceAvailable: boolean;
}

export interface ICoworkingOfferTypeState {
  isPopupOpen: boolean;
  categoryName: string | React.ReactElement | undefined;
}

export interface ICheckBoxButton {
  type: ECoworkingOfferType;
  label: string | React.ReactElement;
  shortLabel?: string;
  disabled?: boolean;
  comingsoon?: boolean;
}

export class CoworkingOfferType extends React.Component<ICoworkingOfferTypeProps, ICoworkingOfferTypeState> {
  public constructor(props: ICoworkingOfferTypeProps) {
    super(props);

    const categoryName = this.getCoworkingOfferTypeName(props.jsonQuery);

    this.state = {
      isPopupOpen: false,
      categoryName,
    };
  }

  public componentWillReceiveProps(nextProps: ICoworkingOfferTypeProps) {
    const dealType = dealTypeFromJsonQuery(this.props.jsonQuery);
    const nextDealType = dealTypeFromJsonQuery(nextProps.jsonQuery);

    const offerType = offerTypeFromJsonQuery(this.props.jsonQuery);
    const nextOfferType = offerTypeFromJsonQuery(nextProps.jsonQuery);

    const coworkingOfferTypeLength = this.props.jsonQuery.coworking_offer_type?.value.length;
    const nextCoworkingOfferTypeLength = nextProps.jsonQuery.coworking_offer_type?.value.length;

    if (
      dealType !== nextDealType ||
      offerType !== nextOfferType ||
      coworkingOfferTypeLength !== nextCoworkingOfferTypeLength
    ) {
      const categoryName = this.getCoworkingOfferTypeName(nextProps.jsonQuery);
      this.setState({
        categoryName,
      });
    }
  }

  public render() {
    const { categoryName, isPopupOpen } = this.state;

    return (
      <div className={style['container']}>
        <OutsideClick rootElement={<div />} onOutsideClick={() => this.setState({ isPopupOpen: false })}>
          <Button
            theme={isPopupOpen ? ButtonTheme.TOGGLE_ACTIVE : ButtonTheme.BLUE}
            buttonStyle={
              mergeStyles(
                style['button'],
                isPopupOpen ? style['button--open'] : undefined,
                this.props.buttonStyle,
                // tslint:disable-next-line: no-any
              ) as any
            }
            onClick={() => this.setState({ isPopupOpen: !this.state.isPopupOpen })}
            dataMark="coworkingOfferType"
          >
            {categoryName}
            <div {...mergeStyles(style['arrow'], isPopupOpen && style['arrow-up'])} />
          </Button>
          {isPopupOpen && (
            <Popup arrow={PopupArrow.LEFT}>
              <div className={style['popupContent']}>
                <div className={style['section']}>
                  <div className={style['section--params']}>{this.getButtons(this.getCoworkingsOptions())}</div>
                </div>
              </div>
            </Popup>
          )}
        </OutsideClick>
      </div>
    );
  }

  private readonly handleOfferTypeChanged = (type: ECoworkingOfferType) => (): void => {
    this.props.onOfferTypeSelected(type);
  };

  private getLabel = (element: ICheckBoxButton) => {
    return (
      <span {...mergeStyles(element.disabled && style['disabled'])}>
        {element.label}
        {element.comingsoon && <span className={style['comingsoon-text']}>Скоро</span>}
      </span>
    );
  };

  private getButtons(settings: ICheckBoxButton[]) {
    const { jsonQuery } = this.props;

    const coworkingOfferType = jsonQuery.coworking_offer_type?.value;

    return settings.map((item, index) => {
      return (
        <CheckboxField
          key={index}
          label={this.getLabel(item)}
          value={coworkingOfferType?.includes(item.type) ?? false}
          onValueChange={this.handleOfferTypeChanged(item.type)}
          containerStyle={style['checkbox-wrapper']}
          disabled={item.disabled}
        />
      );
    });
  }

  private getCoworkingsOptions = (): ICheckBoxButton[] => {
    const { isCoworkingWorkplaceAvailable } = this.props;

    const options = [
      {
        label: 'Отдельный офис',
        shortLabel: 'Отдельный офис',
        type: ECoworkingOfferType.Office,
        disabled: false,
      },
      {
        label: (
          <CheckboxLabel
            label="Закреплённое рабочее место"
            postfixType="info"
            tooltipTitle="Этим столом будете пользоваться только вы"
          />
        ),
        shortLabel: 'Закреп. место',
        type: ECoworkingOfferType.FixedWorkplace,
        disabled: !isCoworkingWorkplaceAvailable,
        comingsoon: !isCoworkingWorkplaceAvailable,
      },
      {
        label: (
          <CheckboxLabel
            label="Незакреплённое рабочее место"
            postfixType="info"
            tooltipTitle="Любое доступное рабочее место в общей зоне"
          />
        ),
        shortLabel: 'Незакреп. место',
        type: ECoworkingOfferType.FlexibleWorkplace,
        disabled: !isCoworkingWorkplaceAvailable,
        comingsoon: !isCoworkingWorkplaceAvailable,
      },
    ];

    return options;
  };

  private getCoworkingOfferTypeShortLabel = (coworkingOfferType: ECoworkingOfferType) => {
    const options = this.getCoworkingsOptions();
    const coworkingOfferTypeOption = options.find(option => option.type === coworkingOfferType);

    return coworkingOfferTypeOption?.shortLabel || coworkingOfferTypeOption?.label;
  };

  private getCoworkingOfferTypeName = (jsonQuery: IJsonQuery): string | React.ReactElement | undefined => {
    const coworkingOfferTypes = jsonQuery.coworking_offer_type?.value;

    if (!coworkingOfferTypes) {
      return undefined;
    }

    if (!this.props.isCoworkingWorkplaceAvailable && coworkingOfferTypes.length > 0) {
      return this.getCoworkingOfferTypeShortLabel(ECoworkingOfferType.Office);
    }

    const optionsLength = this.getCoworkingsOptions().length;
    if (coworkingOfferTypes.length === 0 || coworkingOfferTypes.length === optionsLength) {
      return 'Все типы';
    }

    const firstCoworkingOfferType = this.getCoworkingOfferTypeShortLabel(coworkingOfferTypes[0]);
    if (coworkingOfferTypes.length > 1 && coworkingOfferTypes.length < optionsLength) {
      return `${firstCoworkingOfferType} ...`;
    }

    return firstCoworkingOfferType;
  };
}
